import { useState, useMemo } from "react";
import type { ArvorePiloto, QuadraPiloto, LayerToggle } from "@/types/piloto";

interface PilotMapPanelProps {
  arvores: ArvorePiloto[];
  quadras: QuadraPiloto[];
  camadas: LayerToggle[];
  activeLayers: Set<string>;
  selectedTree: ArvorePiloto | null;
  onSelectTree: (tree: ArvorePiloto) => void;
}

const MAP_BOUNDS = {
  minLat: -16.725,
  maxLat: -16.705,
  minLng: -49.270,
  maxLng: -49.250,
};

function latLngToPercent(lat: number, lng: number): { top: number; left: number } {
  const top = ((MAP_BOUNDS.maxLat - lat) / (MAP_BOUNDS.maxLat - MAP_BOUNDS.minLat)) * 100;
  const left = ((lng - MAP_BOUNDS.minLng) / (MAP_BOUNDS.maxLng - MAP_BOUNDS.minLng)) * 100;
  return { top: Math.max(1, Math.min(99, top)), left: Math.max(1, Math.min(99, left)) };
}

function getRiscoColor(risco: string): string {
  switch (risco) {
    case "baixo": return "bg-primary-500";
    case "medio": return "bg-caution-500";
    case "alto": return "bg-warning-500";
    case "critico": return "bg-danger-500";
    default: return "bg-foreground-400";
  }
}

function getEstadoColor(estado: string): string {
  switch (estado) {
    case "saudavel": return "ring-primary-400";
    case "regular": return "ring-caution-400";
    case "comprometida": return "ring-warning-400";
    case "doente": return "ring-orange-400";
    case "morta": return "ring-danger-400";
    default: return "ring-foreground-300";
  }
}

export default function PilotMapPanel({ arvores, quadras, activeLayers, selectedTree, onSelectTree }: PilotMapPanelProps) {
  const [clustersEnabled] = useState(true);

  const filteredArvores = useMemo(() => {
    if (!activeLayers.has("arvores")) return [];
    return arvores;
  }, [arvores, activeLayers]);

  const visibleQuadras = useMemo(() => {
    if (!activeLayers.has("quadras")) return [];
    return quadras;
  }, [quadras, activeLayers]);

  const clusters = useMemo(() => {
    if (!clustersEnabled || !activeLayers.has("arvores")) return [];
    return [
      { lat: -16.7140, lng: -49.2595, count: 8 },
      { lat: -16.7160, lng: -49.2610, count: 5 },
      { lat: -16.7180, lng: -49.2580, count: 4 },
      { lat: -16.7200, lng: -49.2620, count: 3 },
    ];
  }, [clustersEnabled, activeLayers]);

  return (
    <div className="relative w-full h-full overflow-hidden bg-background-100 rounded-card border border-background-200/70">
      {/* Google Maps embed */}
      <iframe
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3824.027175581527!2d-49.260067!3d-16.7145149!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x935ef0e04c4cd7c7%3A0x5b557e9182f26e47!2sSetor+Bela+Vista%2C+Goi%C3%A2nia+-+GO!5e1!3m2!1spt-BR!2sbr!4v1751300000000!5m2!1spt-BR!2sbr&maptype=satellite"
        className="absolute inset-0 w-full h-full border-0"
        title="Mapa Área Piloto — Setor Bela Vista"
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      ></iframe>

      {/* Subtle overlay */}
      <div className="absolute inset-0 bg-black/5 pointer-events-none"></div>

      {/* Map overlay layer */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Perímetro da área piloto */}
        {activeLayers.has("perimetro") && (
          <div className="absolute inset-[8%] rounded-xl border-3 border-dashed border-primary-500/70 pointer-events-none">
            <span className="absolute -top-5 left-2 text-[9px] font-bold text-primary-600 bg-white/90 px-1.5 py-0.5 rounded">
              Área Piloto SBV
            </span>
          </div>
        )}

        {/* Quadras */}
        {visibleQuadras.slice(0, 20).map((quadra) => {
          const lat = -16.705 - (Math.random() * 0.02);
          const lng = -49.250 - (Math.random() * 0.02);
          const pos = latLngToPercent(lat, lng);
          return (
            <div
              key={quadra.id}
              className="absolute pointer-events-auto cursor-pointer group"
              style={{ top: `${pos.top}%`, left: `${pos.left}%`, width: "6%", height: "4%" }}
              title={quadra.codigo}
            >
              <div className="w-full h-full rounded border border-accent-300/40 bg-accent-100/10 group-hover:bg-accent-100/30 transition-colors flex items-center justify-center">
                <span className="text-[7px] font-semibold text-accent-700 bg-white/80 px-0.5 rounded whitespace-nowrap">
                  {quadra.codigo}
                </span>
              </div>
            </div>
          );
        })}

        {/* Clusters */}
        {clusters.map((cluster, idx) => {
          const pos = latLngToPercent(cluster.lat, cluster.lng);
          return (
            <div
              key={`cluster-${idx}`}
              className="absolute pointer-events-auto cursor-pointer z-10"
              style={{ top: `${pos.top - 1.5}%`, left: `${pos.left - 1}%` }}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-[10px] shadow-md transition-transform hover:scale-110 ${
                cluster.count > 6 ? "bg-warning-500" : cluster.count > 3 ? "bg-accent-500" : "bg-primary-500"
              }`}>
                {cluster.count}
              </div>
            </div>
          );
        })}

        {/* Árvores */}
        {filteredArvores.map((tree) => {
          const pos = latLngToPercent(tree.latitude, tree.longitude);
          const isSelected = selectedTree?.id === tree.id;
          const ringClass = getEstadoColor(tree.estadoFitossanitario);
          const baseColor = getRiscoColor(tree.risco);

          return (
            <button
              key={tree.id}
              className={`absolute cursor-pointer group z-20 pointer-events-auto transition-transform ${isSelected ? "scale-150 z-30" : "hover:scale-125"}`}
              style={{ top: `${pos.top - 0.6}%`, left: `${pos.left - 0.6}%` }}
              onClick={() => onSelectTree(tree)}
              title={`${tree.especiePopular || "Árvore"} — ${tree.codigo}`}
            >
              {isSelected && (
                <div className="absolute inset-0 rounded-full bg-accent-400/40 animate-ping w-4 h-4 -m-0.5"></div>
              )}
              <div className={`w-3 h-3 rounded-full ${baseColor} ring-2 ${ringClass} ring-offset-1 shadow-md relative`}>
                <div className="absolute left-full ml-1.5 top-1/2 -translate-y-1/2 bg-white border border-background-200 rounded-md px-2 py-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-40 min-w-[100px]">
                  <p className="text-[9px] font-semibold text-foreground-900">{tree.especiePopular || "N/I"}</p>
                  <p className="text-[8px] text-foreground-500 font-mono">{tree.codigo}</p>
                  <p className="text-[8px] text-foreground-600">{tree.quadraCodigo}</p>
                </div>
              </div>
            </button>
          );
        })}

        {/* Áreas verdes do entorno */}
        {activeLayers.has("areasverdes") && (
          <>
            <div className="absolute pointer-events-none" style={{ top: "55%", left: "60%", width: "18%", height: "22%" }}>
              <div className="w-full h-full rounded-lg bg-primary-400/10 border-2 border-dashed border-primary-400/40 flex items-start justify-start p-1">
                <span className="text-[8px] font-semibold text-primary-700 bg-white/80 px-1 rounded">Jardim Botânico</span>
              </div>
            </div>
            <div className="absolute pointer-events-none" style={{ top: "15%", left: "70%", width: "14%", height: "16%" }}>
              <div className="w-full h-full rounded-lg bg-primary-400/10 border-2 border-dashed border-primary-400/40 flex items-start justify-start p-1">
                <span className="text-[8px] font-semibold text-primary-700 bg-white/80 px-1 rounded">Parque Areião</span>
              </div>
            </div>
            <div className="absolute pointer-events-none" style={{ top: "30%", left: "80%", width: "12%", height: "14%" }}>
              <div className="w-full h-full rounded-lg bg-primary-400/10 border-2 border-dashed border-primary-400/40 flex items-start justify-start p-1">
                <span className="text-[8px] font-semibold text-primary-700 bg-white/80 px-1 rounded">Parque Flamboyant</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Map legend overlay - bottom left */}
      <div className="absolute bottom-2 left-2 z-20 bg-white/95 backdrop-blur-sm border border-background-200/70 rounded-lg p-2.5 shadow-sm max-w-[180px]">
        <p className="text-[9px] font-semibold text-foreground-700 mb-1.5 font-heading">Legenda</p>
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-primary-500"></div>
            <span className="text-[9px] text-foreground-600">Risco baixo</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-caution-500"></div>
            <span className="text-[9px] text-foreground-600">Risco médio</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-warning-500"></div>
            <span className="text-[9px] text-foreground-600">Risco alto</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-danger-500"></div>
            <span className="text-[9px] text-foreground-600">Risco crítico</span>
          </div>
        </div>
      </div>
    </div>
  );
}